import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import ProtocolCard from '@/components/ProtocolCard';
import { STATE } from '@/constants/testIds/healthspan';
import { Card } from '@/components/ui/card';

const labelize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function Protocols() {
  const [meta, setMeta] = useState(null);
  const [mechanism, setMechanism] = useState('');
  const [evidence, setEvidence] = useState('');
  const [interventionType, setInterventionType] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (mechanism) params.set('mechanism', mechanism);
      if (evidence) params.set('evidence', evidence);
      if (interventionType) params.set('intervention_type', interventionType);
      const { data } = await api.get(`/protocols?${params.toString()}`);
      setData(data);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get('/meta').then(r => setMeta(r.data)).catch(()=>{}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mechanism, evidence, interventionType]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Protocol catalogue</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">Browse all protocols</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-fg)] max-w-3xl">Filter by hallmark mechanism, evidence tier or intervention type. Every entry links to a primary source. Placeholder citations are flagged “Citations pending” and editable by admins.</p>
      </div>

      <Card className="p-4 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] grid sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label>Mechanism</Label>
          <Select value={mechanism} onValueChange={(v)=>setMechanism(v==='__all'?'':v)}>
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All</SelectItem>
              {(meta?.mechanisms || []).map(m => <SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Evidence</Label>
          <Select value={evidence} onValueChange={(v)=>setEvidence(v==='__all'?'':v)}>
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All</SelectItem>
              {(meta?.evidence_levels || []).map(m => <SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Intervention type</Label>
          <Select value={interventionType} onValueChange={(v)=>setInterventionType(v==='__all'?'':v)}>
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All</SelectItem>
              {(meta?.intervention_types || []).map(m => <SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading && (
        <div data-testid={STATE.loading} className="grid md:grid-cols-2 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-40" />)}</div>
      )}
      {error && (
        <Card data-testid={STATE.error} className="p-6 rounded-[var(--radius-md)] border border-[color:var(--danger)] bg-[color:var(--danger-bg)] text-[color:var(--danger)]">{error}</Card>
      )}
      {!loading && data?.protocols?.length === 0 && (
        <Card data-testid={STATE.empty} className="p-8 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-color)] bg-[color:var(--surface)]">
          <div className="text-sm font-medium">No protocols match your filters</div>
          <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Try removing a filter to broaden results.</p>
        </Card>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {data?.protocols?.map(p => (
          <ProtocolCard key={p.id} protocol={p} />
        ))}
      </div>
    </div>
  );
}
